import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Gauge, Ticket, Brain, Users, Phone, Video, Bot, AlertTriangle, ExternalLink } from 'lucide-react';

import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import api from '@/lib/api';
import useAuthStore from '@/stores/authStore';

interface UsageCardProps {
  /** Narrow sidebar — icon only. */
  collapsed?: boolean;
}

interface Quota { used: number; included: number | null; over: number; unit_cents: number | null; amount_cents: number; }
interface V2 {
  plan: string; plan_version: number; plan_name: string; currency: string | null; limit_mode: 'overage' | 'block';
  period: { start: string; end: string };
  quotas: Record<string, Quota>;
  projected_overage_cents: number;
  in_grace?: boolean;
}
interface Billing {
  billable: boolean; billed_overage_cents: number; open_overage_cents: number; projected_overage_cents: number;
  runs: { run_date: string; status: string; total_cents: number }[];
}

const KINDS: { key: string; icon: any; label: [string, string] }[] = [
  { key: 'tickets', icon: Ticket, label: ['shell:usage.tickets', 'Anfragen'] },
  { key: 'ai_interactions', icon: Brain, label: ['shell:usage.ai', 'AI-Interaktionen'] },
  { key: 'seats', icon: Users, label: ['shell:usage.seats', 'Seats'] },
  { key: 'phone_human_minutes', icon: Phone, label: ['shell:usage.phoneHuman', 'Telefon'] },
  { key: 'phone_ai_minutes', icon: Phone, label: ['shell:usage.phoneAi', 'AI Phone'] },
  { key: 'meeting_participant_minutes', icon: Video, label: ['shell:usage.meetings', 'Meeting-Min.'] },
  { key: 'chatbot_conversations', icon: Bot, label: ['shell:usage.chatbot', 'Chatbot'] },
];

const money = (cents: number, currency: string | null) => `${currency === 'eur' ? '€' : '$'}${(cents / 100).toFixed(2)}`;

/**
 * Usage card in the sidebar — same look as the Meetings/Phone cards.
 * Popover: every metered quota of the current billing period (used / included / remaining),
 * and — if the plan was exceeded — the overage charges (already charged / open, charged nightly).
 */
export default function UsageCard({ collapsed = false }: UsageCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === 'admin';

  const usageQ = useQuery({
    queryKey: ['license-usage-card'],
    queryFn: async () => (await api.get('/license/usage')).data?.data?.v2 as V2 | null,
    refetchInterval: 5 * 60_000,
    staleTime: 60_000,
    retry: false,
  });
  const billingQ = useQuery({
    queryKey: ['license-billing-card'],
    queryFn: async () => (await api.get('/license/billing-summary')).data?.data as Billing | null,
    enabled: open && isAdmin && usageQ.data?.plan_version === 2,
    staleTime: 60_000,
    retry: false,
  });

  const v2 = usageQ.data;
  // Self-hosted / legacy plans have no metered quotas → card stays hidden
  if (!v2 || v2.plan_version !== 2) return null;

  const q = v2.quotas || {};
  const rows = KINDS.map((k) => ({ ...k, q: q[k.key] })).filter((r) => r.q && (r.q.included !== null || r.q.used > 0));
  const overKinds = rows.filter((r) => (r.q?.over || 0) > 0);
  const worst = rows.reduce((m, r) => {
    if (!r.q || r.q.included === null || r.q.included === 0) return m;
    return Math.max(m, r.q.used / r.q.included);
  }, 0);
  const overage = v2.projected_overage_cents > 0;
  const tone = overage ? 'over' : worst >= 0.8 ? 'warn' : 'ok';
  const pctLabel = `${Math.min(999, Math.round(worst * 100))}%`;
  const periodEnd = new Date(v2.period.end);
  const daysLeft = Math.max(0, Math.ceil((periodEnd.getTime() - Date.now()) / 86_400_000));
  const billing = billingQ.data;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-live="false"
          className={cn(
            'vyndesk-call-status flex items-center gap-2 rounded-md border text-xs font-medium transition',
            collapsed ? 'h-8 w-8 justify-center px-0' : 'h-9 w-full px-2.5',
          )}
          title={t('shell:usage.title', 'Nutzung')}
        >
          <Gauge className="h-3.5 w-3.5 shrink-0" />
          {!collapsed && (
            <>
              <span className="min-w-0 flex-1 truncate text-left">{t('shell:usage.title', 'Nutzung')}</span>
              <span
                className={cn(
                  'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold tabular-nums',
                  tone === 'over' ? 'bg-red-100 text-red-700' : tone === 'warn' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700',
                )}
              >
                {overage ? money(v2.projected_overage_cents, v2.currency) : pctLabel}
              </span>
            </>
          )}
          {collapsed && tone !== 'ok' && (
            <span className={cn('absolute -mt-4 ml-4 h-2 w-2 rounded-full', tone === 'over' ? 'bg-red-500' : 'bg-amber-500')} />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent side="right" align="end" className="w-80 rounded-md p-3">
        <div className="mb-2 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-foreground">
              {t('shell:usage.planLine', 'VynDesk {{plan}}', { plan: v2.plan_name || v2.plan })}
            </div>
            <div className="text-[10px] text-muted-foreground">
              {t('shell:usage.periodLeft', 'Zeitraum endet in {{days}} Tagen', { days: daysLeft })}
            </div>
          </div>
          {v2.limit_mode === 'block' && (
            <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
              {t('shell:usage.blockMode', 'Stopp am Limit')}
            </span>
          )}
        </div>

        {/* Quotas */}
        <ul className="space-y-1.5">
          {rows.map(({ key, icon: Icon, label, q: quota }) => {
            if (!quota) return null;
            const unlimited = quota.included === null;
            const pct = unlimited || !quota.included ? 0 : Math.min(100, Math.round((quota.used / quota.included) * 100));
            const remaining = unlimited ? null : Math.max(0, (quota.included || 0) - quota.used);
            const isOver = quota.over > 0;
            return (
              <li key={key} className="rounded-md border border-border px-2 py-1.5">
                <div className="flex items-center gap-2">
                  <Icon className={cn('h-3.5 w-3.5 shrink-0', isOver ? 'text-red-500' : 'text-muted-foreground')} />
                  <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground">{t(label[0], label[1])}</span>
                  <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                    {quota.used.toLocaleString()} / {unlimited ? '∞' : (quota.included || 0).toLocaleString()}
                  </span>
                </div>
                {!unlimited && (
                  <>
                    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className={cn('h-full rounded-full', isOver ? 'bg-red-500' : pct >= 80 ? 'bg-amber-500' : 'bg-emerald-500')}
                        style={{ width: `${isOver ? 100 : pct}%` }}
                      />
                    </div>
                    <div className="mt-0.5 flex justify-between text-[10px] text-muted-foreground">
                      {isOver ? (
                        <span className="text-red-600">
                          {t('shell:usage.overBy', '{{n}} über dem Paket', { n: quota.over.toLocaleString() })}
                          {quota.unit_cents ? ` · ${money(quota.amount_cents, v2.currency)}` : ''}
                        </span>
                      ) : (
                        <span>{t('shell:usage.remaining', '{{n}} übrig', { n: (remaining || 0).toLocaleString() })}</span>
                      )}
                      <span>{pct}%</span>
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>

        {/* Overage / penalties */}
        {overage && (
          <div className="mt-2 rounded-md border border-red-200 bg-red-50 px-2.5 py-2 dark:border-red-500/30 dark:bg-red-500/10">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-red-700 dark:text-red-300">
              <AlertTriangle className="h-3.5 w-3.5" />
              {t('shell:usage.overageTitle', 'Nutzung über Paket: {{amount}}', { amount: money(v2.projected_overage_cents, v2.currency) })}
            </div>
            {isAdmin && billing ? (
              <div className="mt-1 space-y-0.5 text-[10px] text-red-800/90 dark:text-red-200/90">
                <div className="flex justify-between"><span>{t('shell:usage.charged', 'Bereits abgebucht')}</span><b>{money(billing.billed_overage_cents, v2.currency)}</b></div>
                <div className="flex justify-between"><span>{t('shell:usage.open', 'Offen')}</span><b>{money(billing.open_overage_cents, v2.currency)}</b></div>
                <div className="pt-0.5 text-red-700/80 dark:text-red-200/70">
                  {billing.billable
                    ? t('shell:usage.nightly', 'Offene Beträge werden täglich um Mitternacht von der hinterlegten Karte abgebucht.')
                    : t('shell:usage.notBillable', 'Keine automatische Abbuchung (kein aktives Abo).')}
                </div>
              </div>
            ) : (
              <div className="mt-1 text-[10px] text-red-700/80 dark:text-red-200/70">
                {overKinds.map((r) => t(r.label[0], r.label[1])).join(' · ')}
              </div>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => { setOpen(false); navigate('/settings/license'); }}
          className="mt-2 flex w-full items-center justify-center gap-1 text-center text-[11px] font-medium text-muted-foreground hover:text-foreground"
        >
          {t('shell:usage.details', 'Details & Paket')} <ExternalLink className="h-3 w-3" />
        </button>
      </PopoverContent>
    </Popover>
  );
}
